<script setup lang="ts">
const { locale, locales, t } = useI18n();
const switchLocalePath = useSwitchLocalePath();
const { getItems } = useDirectusItems();
const { directusLocale } = useGlobalData();
const isMobileMenuOpen = ref(false);
const route = useRoute();
const colorMode = useColorMode();
const isScrolled = ref(false);
const router = useRouter();
const lastScrollY = ref(0);

const colorModeIcon = computed(() => {
    return colorMode.value === 'dark' ? 'i-heroicons-sun' : 'i-heroicons-moon';
});

interface Translation {
    menu_title: string;
    languages_code: string;
}

interface Page {
    id: string;
    slug: string;
    menu_title: string;
    translations: Translation[];
}

const availableLocales = computed(() => {
    // hide in menu for now
    return [];

    return locales.value.filter((i) => i.code !== locale.value);
});

// Load pages for current locale only
const { data: menuPages, refresh: refreshMenuPages } = await useAsyncData('menu-pages', async () => {
    const response = await getItems<Page[]>({
        collection: 'pages',
        params: {
            fields: ['id', 'slug', 'menu_title', 'translations.menu_title', 'translations.languages_code'],
            deep: {
                translations: {
                    _filter: {
                        languages_code: { _eq: directusLocale.value },
                    },
                },
            },
            filter: {
                menu_title: { _nnull: true },
                status: { _eq: 'published' },
            },
        },
    });
    return response as unknown as Page[];
});

// Refresh menu pages when locale changes
watch(locale, () => {
    refreshMenuPages();
});

// Close mobile menu when route changes
watch(
    () => route.path,
    () => {
        isMobileMenuOpen.value = false;
    }
);

// Add scroll listener
onMounted(() => {
    if (typeof window !== 'undefined') {
        window.addEventListener('scroll', handleScroll);
    }
});

onUnmounted(() => {
    if (typeof window !== 'undefined') {
        window.removeEventListener('scroll', handleScroll);
    }
});

const handleScroll = () => {
    if (typeof window !== 'undefined') {
        const currentScrollY = window.scrollY;

        // Use hysteresis to prevent flickering
        if (currentScrollY > lastScrollY.value) {
            // Scrolling down
            if (currentScrollY > 50) {
                isScrolled.value = true;
            }
        } else {
            // Scrolling up
            if (currentScrollY < 30) {
                isScrolled.value = false;
            }
        }

        lastScrollY.value = currentScrollY;
    }
};
</script>

<template>
    <div
        class="sticky z-50 top-4 mb-8 flex flex-row gap-2 justify-between items-center px-4 py-2 transition-all duration-300 mx-2 xl:mx-auto xl:max-w-7xl sm:w-full"
        :class="{
            'bg-white/90 backdrop-blur-md shadow-lg rounded-lg': isScrolled,
            'bg-transparent': !isScrolled,
        }"
    >
        <div
            class="logo flex flex-row gap-0 items-center px-4 py-1 rounded cursor-pointer h-20"
            @click="router.push('/')"
        >
            <img v-if="!isScrolled" src="~/assets/img/RadiMed_balts.svg" alt="RadiMed" class="w-60 h-20" />
            <img v-else src="~/assets/img/RadiMed_2tone.svg" alt="RadiMed" class="w-40 h-10" />
        </div>

        <!-- Desktop Menu -->
        <div
            class="flex-row gap-4 gap-x-12 hidden lg:flex mx-4"
            :class="{
                'text-blue-600 dark:text-white': isScrolled,
                'text-gray-100': !isScrolled,
            }"
        >
            <NuxtLink
                :to="'/' + locale"
                class="uppercase font-bold relative after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-current after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-300"
                :class="{
                    'cursor-default after:scale-x-100': route.path === '/' + locale || route.path === '/',
                    '': route.path !== '/' + locale && route.path !== '/',
                }"
                :tabindex="route.path === '/' + locale ? -1 : 0"
            >
                {{ t('menu.home') }}
            </NuxtLink>
            <NuxtLink
                v-for="page in menuPages"
                :key="page.slug"
                :to="'/' + locale + '/' + page.slug"
                class="uppercase font-bold relative after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-current after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-300"
                :class="{
                    'cursor-default after:scale-x-100': route.path === '/' + locale + '/' + page.slug,
                    '': route.path !== '/' + locale + '/' + page.slug,
                }"
                :tabindex="route.path === '/' + locale + '/' + page.slug ? -1 : 0"
            >
                {{ page.translations[0]?.menu_title || page.menu_title }}
            </NuxtLink>
        </div>
        <div class="flex-row gap-4 hidden lg:flex items-center" v-if="availableLocales.length > 0">
            <NuxtLink
                v-for="lang in availableLocales"
                :key="lang.code"
                :to="switchLocalePath(lang.code)"
                class="text-sm"
                :class="{
                    'text-blue-600 dark:text-white': isScrolled,
                    'text-gray-100': !isScrolled,
                }"
            >
                {{ lang.title || lang.code }}
            </NuxtLink>
        </div>
        <div v-else>&nbsp;</div>

        <!-- Mobile Menu -->
        <USlideover
            v-model:open="isMobileMenuOpen"
            title=""
            class="lg:hidden"
            :close="{
                color: 'primary',
                class: 'text-2xl',
            }"
            :ui="{ overlay: 'bg-offblue-500/70', content: 'bg-white/95', header: 'border-0 text-2xl' }"
        >
            <UButton
                icon="i-heroicons-bars-3"
                variant="ghost"
                class="text-2xl"
                :class="isScrolled ? 'text-blue-600' : 'text-white'"
            />

            <template #body class="bg-white/80">
                <div class="flex flex-col justify-between p-4 h-full">
                    <div class="flex flex-col gap-4">
                        <NuxtLink
                            :to="'/' + locale"
                            class="text-xl uppercase"
                            :class="{
                                'text-gray-900 dark:text-white cursor-default after:scale-x-100':
                                    route.path === '/' + locale,
                                'text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300':
                                    route.path !== '/' + locale,
                            }"
                            :tabindex="route.path === '/' + locale ? -1 : 0"
                        >
                            {{ t('menu.home') }}
                        </NuxtLink>
                        <NuxtLink
                            v-for="page in menuPages"
                            :key="page.slug"
                            :to="'/' + locale + '/' + page.slug"
                            class="text-xl uppercase"
                            :class="{
                                'text-gray-900 dark:text-white cursor-default after:scale-x-100':
                                    route.path === '/' + locale + '/' + page.slug,
                                'text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300':
                                    route.path !== '/' + locale + '/' + page.slug,
                            }"
                            :tabindex="route.path === '/' + locale + '/' + page.slug ? -1 : 0"
                        >
                            {{ page.translations[0]?.menu_title || page.menu_title }}
                        </NuxtLink>
                    </div>

                    <div class="mt-auto pt-4 border-t border-gray-200 dark:border-gray-700">
                        <div class="flex flex-row gap-2 justify-between items-center">
                            <NuxtLink
                                v-for="lang in availableLocales"
                                :key="lang.code"
                                :to="switchLocalePath(lang.code)"
                                class="text-md text-gray-600"
                            >
                                {{ lang.title || lang.code }}
                            </NuxtLink>
                        </div>
                    </div>
                </div>
            </template>
        </USlideover>
        <!-- END Mobile Menu -->
    </div>
</template>
